import React, { useState } from 'react';
import { css, useTheme } from '@emotion/react';
// @ts-ignore - ContextMenu is a JS component without types
import {
  ContextMenu as ContextMenuUntyped,
  contextStyles,
} from '@birdi/context-menu/ContextMenu';

// The deprecated ContextMenu HOC has incomplete types (children-as-function,
// alignRight, etc. are runtime props) — treat it as untyped like other callers.
const ContextMenu = ContextMenuUntyped as React.ComponentType<any>;
import EditSVG from '@birdi/icons/edit-3.svg';
import MapPinSVG from '@birdi/icons/map-pin.svg';
import ImageSVG from '@birdi/icons/image.svg';
import TrashSVG from '@birdi/icons/trash.svg';
import { useMetaSelector } from '../meta/reducer';
import {
  RenameSiteModal,
  UpdateAddressModal,
  ArchiveSiteModal,
  ChangeSiteImageModal,
} from './site-modals';

interface SiteMenuProps {
  site: {
    id: string;
    name: string;
    address?: string | null;
    s3ImageUrl?: string | null;
  };
  /** Called after the site was archived (e.g. navigate away from SitePage) */
  onArchived?: () => void;
}

/**
 * The `⋯` menu shared by All Sites cards/rows and the Site page header:
 * Rename site / Update address / Change cover photo / Archive site.
 */
export const SiteMenu: React.FC<SiteMenuProps> = ({ site, onArchived }) => {
  const theme = useTheme();
  const meta = useMetaSelector();
  const [renameOpen, setRenameOpen] = useState(false);
  const [addressOpen, setAddressOpen] = useState(false);
  const [imageOpen, setImageOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);

  const isOps = meta.opsActions.includes('access_ops');
  const canEdit = meta.permissions.includes('workspace:map:create') || isOps;
  const canDelete = meta.permissions.includes('workspace:map:delete') || isOps;

  if (!canEdit && !canDelete) return null;

  const itemCSS = css`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 0.6em;
    width: 100%;
    text-align: left;
    white-space: nowrap;
    svg {
      width: 1.15em;
      height: 1.15em;
      flex-shrink: 0;
    }
  `;

  return (
    <>
      <ContextMenu alignRight data-testid="site-menu">
        {(props: { hide: () => void }) => (
          <div css={contextStyles(theme)}>
            {canEdit && (
              <button
                type="button"
                css={itemCSS}
                data-testid="site-menu-rename"
                onClick={() => {
                  props.hide();
                  setRenameOpen(true);
                }}
              >
                <EditSVG />
                <span>Rename site</span>
              </button>
            )}
            {canEdit && (
              <button
                type="button"
                css={itemCSS}
                data-testid="site-menu-update-address"
                onClick={() => {
                  props.hide();
                  setAddressOpen(true);
                }}
              >
                <MapPinSVG />
                <span>Update address</span>
              </button>
            )}
            {canEdit && (
              <button
                type="button"
                css={itemCSS}
                data-testid="site-menu-change-image"
                onClick={() => {
                  props.hide();
                  setImageOpen(true);
                }}
              >
                <ImageSVG />
                <span>Change cover photo</span>
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                css={itemCSS}
                data-testid="site-menu-delete"
                onClick={() => {
                  props.hide();
                  setArchiveOpen(true);
                }}
              >
                <TrashSVG />
                <span>Archive site</span>
              </button>
            )}
          </div>
        )}
      </ContextMenu>
      {renameOpen && (
        <RenameSiteModal
          open={renameOpen}
          setOpen={setRenameOpen}
          site={site}
        />
      )}
      {addressOpen && (
        <UpdateAddressModal
          open={addressOpen}
          setOpen={setAddressOpen}
          site={site}
        />
      )}
      {imageOpen && (
        <ChangeSiteImageModal
          open={imageOpen}
          setOpen={setImageOpen}
          site={site}
        />
      )}
      {archiveOpen && (
        <ArchiveSiteModal
          open={archiveOpen}
          setOpen={setArchiveOpen}
          site={site}
          onArchived={onArchived}
        />
      )}
    </>
  );
};
